/* operations.jsx - document register screen helpers. */
(function () {
  const { useState, useMemo } = React;

  function fmtDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return window.AC.fmtDate(value);
    return new Intl.DateTimeFormat("en-GB", { dateStyle: "short", timeStyle: "short" }).format(date);
  }

  function documentTargetLabel(row) {
    if (row.ticketTitle) return `Ticket - ${row.ticketTitle}`;
    if (row.milestoneTitle) return `Milestone - ${row.milestoneTitle}`;
    if (row.workPackageName) return `${row.projectKind === "Implementation" ? "Work package" : "Section"} - ${row.workPackageName}`;
    if (row.kind === "Unlinked") return "Unlinked";
    return row.kind === "Expense" ? "Expense" : "Project base";
  }

  function documentAiContextReference(row) {
    const projectUrl = row.projectId ? `${window.location.origin}${window.location.pathname}#/projects/${encodeURIComponent(row.projectId)}/context` : "";
    return {
      type: "Document",
      id: row.documentId || row.storageDocumentId || row.expenseId || row.key,
      label: row.fileName || "Untitled document",
      url: row.externalUrl || projectUrl,
      fields: {
        Owner: row.projectName || row.kind || "",
        Kind: row.kind || "",
        ProjectId: row.projectId || "",
        DocumentId: row.documentId || "",
        StorageDocumentId: row.storageDocumentId || "",
        ExpenseId: row.expenseId || "",
        Target: documentTargetLabel(row),
        Description: row.description || "",
        ContentType: row.contentType || "",
        Source: row.externalProvider || "Local file",
        ExternalFileId: row.externalFileId || "",
        ExternalPath: row.externalPath || "",
        UploadedAt: row.uploadedAt || "",
        ExternalModifiedAt: row.externalModifiedAt || "",
        Requirement: row.isRequirement ? "Yes" : "",
      },
    };
  }

  function projectDocumentRows(db) {
    const storageDocuments = db.storageDocuments || [];
    return (db.projects || []).flatMap((project) => {
      const tickets = MODEL.projectTickets(project);
      return (project.documents || []).map((document) => {
        const ticket = document.ticketId ? tickets.find((item) => item.id === document.ticketId) : null;
        const milestone = document.milestoneId ? (project.milestones || []).find((item) => item.id === document.milestoneId) : null;
        const workPackage = document.workPackageId ? (project.workPackages || []).find((item) => item.id === document.workPackageId) : null;
        const storageDocument = storageDocuments.find((item) => item.linkedProjectId === project.id && item.linkedDocumentId === document.id);
        return {
          kind: "Project",
          key: `project:${project.id}:${document.id}`,
          projectId: project.id,
          documentId: document.id,
          storageDocumentId: storageDocument ? storageDocument.id : null,
          projectName: project.name,
          projectKind: project.kind,
          fileName: document.fileName,
          description: document.description || "",
          contentType: document.contentType || "",
          uploadedAt: document.uploadedAt,
          externalProvider: document.externalProvider || "",
          externalFileId: document.externalFileId || "",
          externalPath: document.externalPath || "",
          externalUrl: document.externalUrl || "",
          externalModifiedAt: document.externalModifiedAt,
          externalSyncStatus: document.externalSyncStatus || "",
          externalSyncedAt: document.externalSyncedAt,
          storageConnectionId: document.storageConnectionId || null,
          isRequirement: !!document.isRequirement,
          ticketTitle: ticket && ticket.title,
          milestoneTitle: milestone && milestone.title,
          workPackageName: workPackage && workPackage.name,
        };
      });
    });
  }

  function expenseDocumentRows(db) {
    return (db.expenses || []).filter((expense) => expense.document).map((expense) => ({
      kind: "Expense",
      key: `expense:${expense.id}`,
      expenseId: expense.id,
      fileName: expense.document.fileName,
      description: expense.description || expense.vendor || expense.category || "",
      contentType: expense.document.contentType || "",
      uploadedAt: expense.document.uploadedAt,
      externalProvider: "",
      externalFileId: "",
      externalPath: "",
      externalUrl: "",
      externalModifiedAt: null,
      externalSyncStatus: "",
      externalSyncedAt: null,
      storageConnectionId: null,
      isRequirement: false,
    }));
  }

  function storageDocumentRows(db) {
    return (db.storageDocuments || []).filter((document) => !document.linkedProjectId).map((document) => ({
      kind: "Unlinked",
      key: `storage:${document.id}`,
      storageDocumentId: document.id,
      storageConnectionId: document.storageConnectionId || null,
      fileName: document.fileName,
      description: "Imported from storage base folder",
      contentType: document.contentType || "",
      uploadedAt: document.importedAt,
      externalProvider: document.provider || "",
      externalFileId: document.externalFileId || "",
      externalPath: document.externalPath || "",
      externalUrl: document.externalUrl || "",
      externalModifiedAt: document.externalModifiedAt,
      externalSyncStatus: "Imported",
      externalSyncedAt: document.importedAt,
      isRequirement: false,
    }));
  }

  function storageConnectionLabel(connection) {
    return `${connection.provider || "storage"} - ${connection.accountName || connection.accountId || "connected account"}`;
  }

  function promptProject(db) {
    const projects = db.projects || [];
    if (projects.length === 0) return null;
    const text = projects.map((project, index) => `${index + 1}. ${project.name}`).join('\n');
    const answer = window.prompt(`Attach to project:\n${text}`, "1");
    if (answer === null) return null;
    const index = Number.parseInt(answer, 10) - 1;
    return projects[index] || null;
  }

  function promptStorageConnection(db) {
    const connections = db.storageConnections || [];
    if (connections.length === 0) return null;
    const text = ["0. Manual URL", ...connections.map((connection, index) => `${index + 1}. ${storageConnectionLabel(connection)}`)].join('\n');
    const answer = window.prompt(`External source:\n${text}`, connections.length ? "1" : "0");
    if (answer === null) return undefined;
    const index = Number.parseInt(answer, 10);
    return index > 0 ? connections[index - 1] || null : null;
  }

  function promptBaseStorageConnection(db) {
    const connections = (db.storageConnections || []).filter((connection) => connection.authorized && (connection.baseFolderId || connection.baseFolderPath));
    if (connections.length === 0) {
      window.alert('Set a base folder on a connected storage account first.');
      return null;
    }

    const text = connections.map((connection, index) => `${index + 1}. ${storageConnectionLabel(connection)} - ${connection.baseFolderName || 'base folder'}`).join('\n');
    const answer = window.prompt(`Import from storage base folder:\n${text}`, "1");
    if (answer === null) return null;
    const index = Number.parseInt(answer, 10) - 1;
    return connections[index] || null;
  }

  async function attachExternalFromDocumentsView(db, onAction) {
    if (!onAction) return;
    const project = promptProject(db);
    if (!project) return;
    const connection = promptStorageConnection(db);
    if (connection === undefined) return;
    const externalUrl = window.prompt('External document URL', '');
    if (externalUrl === null) return;
    const cleanUrl = externalUrl.trim();
    let fileName = '';
    try {
      const parsed = new URL(cleanUrl);
      fileName = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '');
    } catch (_) {
      fileName = '';
    }

    fileName = window.prompt('Document name', fileName || 'External document');
    if (fileName === null || !fileName.trim()) return;
    const externalFileId = window.prompt('Provider file id (recommended for OAuth sync)', '');
    if (externalFileId === null) return;
    const externalPath = window.prompt('External path or note', '');
    if (externalPath === null) return;
    await onAction('attachExternalProjectDocument', {
      projectId: project.id,
      fileName: fileName.trim(),
      provider: connection ? connection.provider : 'External',
      externalUrl: cleanUrl,
      externalFileId: externalFileId.trim(),
      externalPath: externalPath.trim(),
      storageConnectionId: connection ? connection.id : null,
    });
  }

  async function importFromBaseFolder(db, onAction) {
    if (!onAction) return;
    const connection = promptBaseStorageConnection(db);
    if (!connection) return;
    await onAction('importStorageBaseFolderDocuments', {
      storageConnectionId: connection.id,
    });
  }

  function OperationsDocuments({ db, query, onAction, onToast, onError, onReload }) {
    const [source, setSource] = useState("all");
    const [syncing, setSyncing] = useState("");
    const [draggingDocumentId, setDraggingDocumentId] = useState("");
    const rows = useMemo(() => {
      const q = String(query || "").trim().toLowerCase();
      return [...storageDocumentRows(db), ...projectDocumentRows(db), ...expenseDocumentRows(db)]
        .filter((row) => {
          if (source === "external" && !row.externalProvider) return false;
          if (source === "local" && row.externalProvider) return false;
          if (source === "unlinked" && row.kind !== "Unlinked") return false;
          if (!q) return true;
          return [
            row.fileName,
            row.description,
            row.projectName,
            row.externalProvider,
            row.externalFileId,
            row.externalPath,
            row.externalUrl,
            row.contentType,
          ].some((value) => String(value || "").toLowerCase().includes(q));
        })
        .sort((a, b) => String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || "")));
    }, [db, query, source]);
    const externalCount = rows.filter((row) => row.externalProvider).length;
    const unlinkedCount = rows.filter((row) => row.kind === "Unlinked").length;
    const syncExternal = async (row) => {
      if (!row.projectId || !row.documentId || !onAction) return;
      try {
        setSyncing(row.key);
        await onAction("syncExternalProjectDocument", { projectId: row.projectId, documentId: row.documentId });
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setSyncing("");
      }
    };
    const openDocument = (row) => {
      if (!onAction) return;
      if (row.kind === "Unlinked" && row.externalUrl) { window.open(row.externalUrl, "_blank", "noreferrer"); return; }
      if (row.kind === "Unlinked") return;
      if (row.kind === "Expense") onAction("doc", { id: row.expenseId });
      else onAction("openProjectDocument", { projectId: row.projectId, documentId: row.documentId });
    };
    const linkStorageDocument = async (storageDocumentId, projectId) => {
      if (!storageDocumentId || !projectId || !onAction) return;
      try {
        setSyncing(`link:${storageDocumentId}:${projectId}`);
        await onAction("linkStorageDocumentToProject", { storageDocumentId, projectId });
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setSyncing("");
        setDraggingDocumentId("");
      }
    };
    const unlinkStorageDocument = async (row) => {
      if (!row.storageDocumentId || !onAction) return;
      try {
        setSyncing(`unlink:${row.storageDocumentId}`);
        await onAction("unlinkStorageDocumentFromProject", { storageDocumentId: row.storageDocumentId });
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setSyncing("");
      }
    };
    const attachProjectDocument = async (documentId, projectId) => {
      if (!documentId || !projectId || !onAction) return;
      try {
        setSyncing(`attach:${documentId}:${projectId}`);
        await onAction("attachExistingProjectDocument", { documentId, projectId });
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setSyncing("");
      }
    };

    return (
      <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
        {({ contextMenuProps }) => (
          <div className="ops-doc-layout">
            <div className="ops-doc-main">
            <Toolbar>
              <Segmented value={source} onChange={setSource} options={[
                { value: "all", label: "All" },
                { value: "unlinked", label: "Unlinked" },
                { value: "external", label: "External" },
                { value: "local", label: "Local" },
              ]} />
              <span className="muted" style={{ fontSize: 12 }}>{rows.length} document{rows.length === 1 ? "" : "s"} - {unlinkedCount} unlinked - {externalCount} external</span>
              <div style={{ marginLeft: "auto" }}>
                <button className="btn sm ghost" onClick={() => importFromBaseFolder(db, onAction)}><Icon name="download" size={13} />Import base folder</button>
                <button className="btn sm ghost" onClick={() => attachExternalFromDocumentsView(db, onAction)}><Icon name="link" size={13} />Link external</button>
                <button className="btn sm ghost" onClick={() => onReload && onReload()}><Icon name="refresh" size={13} />Refresh</button>
              </div>
            </Toolbar>
            <Panel title="Document register" sub="Project documents, expense receipts, and linked external sources" flush>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Owner</th>
                      <th>Target</th>
                      <th>Source</th>
                      <th>Uploaded</th>
                      <th>Modified</th>
                      <th>Sync</th>
                      <th style={{ width: 1 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.key}
	                        draggable={row.kind === "Unlinked" || row.kind === "Project"}
	                        {...contextMenuProps(documentAiContextReference(row))}
	                        onDragStart={(event) => {
	                      if (row.kind === "Unlinked") {
	                        event.dataTransfer.setData("application/x-appligator-storage-document-id", row.storageDocumentId);
	                        event.dataTransfer.setData("text/plain", row.storageDocumentId);
	                      } else if (row.kind === "Project") {
	                        event.dataTransfer.setData("application/x-appligator-document-id", row.documentId);
	                        event.dataTransfer.setData("text/plain", row.documentId);
	                      } else {
	                        return;
	                      }
	                      event.dataTransfer.effectAllowed = "move";
	                      setDraggingDocumentId(row.storageDocumentId || row.documentId);
	                        }}
                        onDragEnd={() => setDraggingDocumentId("")}
                      >
                        <td>
                          <span className="cellstack">
                            <span className="t-strong"><Icon name={row.externalProvider ? "link" : "doc"} size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />{row.fileName}</span>
                            <span className="t-sub">{row.description || row.contentType || "-"}</span>
                          </span>
                        </td>
                        <td>
                          <span className="cellstack">
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span>{row.projectName || row.kind}</span>
                              {row.kind === "Project" && row.storageDocumentId && (
                                <button className="icon-btn" title="Unlink from project" onClick={(event) => { event.stopPropagation(); unlinkStorageDocument(row); }} disabled={syncing === `unlink:${row.storageDocumentId}`}>
                                  <Icon name="unlink" size={13} />
                                </button>
                              )}
                            </span>
                            <span className="t-sub">{row.kind}</span>
                          </span>
                        </td>
                        <td>{documentTargetLabel(row)}</td>
                        <td>
                          {row.externalProvider ? (
                            <span className="cellstack">
                              <span className="pill sm"><span className="dot" />{row.externalProvider}</span>
                              <span className="t-sub" title={row.externalPath || row.externalUrl}>{row.externalPath || row.externalUrl || row.externalFileId || "-"}</span>
                            </span>
                          ) : <span className="muted">Local file</span>}
                        </td>
                        <td>{fmtDateTime(row.uploadedAt)}</td>
                        <td>{fmtDateTime(row.externalModifiedAt)}</td>
                        <td>
                          {row.externalProvider ? (
                            <span className="cellstack">
                              <span className={row.externalSyncStatus === "ok" ? "pill paid sm" : "pill sm"}>{row.externalSyncStatus || "Not synced"}</span>
                              <span className="t-sub">{fmtDateTime(row.externalSyncedAt)}</span>
                            </span>
                          ) : <span className="muted">-</span>}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                            {row.externalProvider && (
                              <button className="icon-btn" title={row.externalUrl ? "Sync external metadata" : "Sync requires an external URL"} onClick={() => syncExternal(row)} disabled={syncing === row.key}>
                                <Icon name="refresh" size={14} />
                              </button>
                            )}
                            <button className="icon-btn" title="Open document" onClick={() => openDocument(row)} disabled={row.kind === "Unlinked" && !row.externalUrl}><Icon name="external" size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan="8"><Empty>No documents match this view.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>
            </div>
            <aside className="ops-doc-projects">
	              <Panel title="Link to project" sub="Drag documents here" flush>
                <div className="ops-doc-project-list">
                  {(db.projects || []).map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      className={draggingDocumentId ? "ops-doc-project-drop active" : "ops-doc-project-drop"}
                      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}
	                      onDrop={(event) => {
	                        event.preventDefault();
	                        const storageDocumentId = event.dataTransfer.getData("application/x-appligator-storage-document-id");
	                        const documentId = event.dataTransfer.getData("application/x-appligator-document-id");
	                        if (storageDocumentId) linkStorageDocument(storageDocumentId, project.id);
	                        else attachProjectDocument(documentId || event.dataTransfer.getData("text/plain"), project.id);
	                      }}
                    >
                      <span>{project.name}</span>
                      <small>{project.kind || "Project"}</small>
                    </button>
                  ))}
                  {(db.projects || []).length === 0 && <Empty>No projects available.</Empty>}
                </div>
              </Panel>
            </aside>
          </div>
        )}
      </AiEmployeeContextActions>
    );
  }

  Object.assign(window, { OperationsDocuments });
})();
